import React, { useState } from 'react';
import { Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useInvalidateLineRead, useLineActionCall, useLineRead, type Obj } from '@/client/api/whatsapp/line-actions';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ActionButton, asArray, ConfirmDialog, digitsOf, Empty, ErrorNote, Loading, Section, selectCls, toastError } from './shared';

// WhatsApp label colour indexes 0..19 (Evolution returns the index).
const COLOURS = [
  '#ff9485', '#64c4ff', '#ffd429', '#dfaef0', '#99b6c1', '#55ccb3', '#ff9dff', '#d3a91d', '#6d7cce', '#d7e752',
  '#00d0e2', '#ffc5c7', '#93ceac', '#f74848', '#00a0f2', '#83e422', '#ffaf04', '#b5ebff', '#9ba6ff', '#9368cf',
];
const colourOf = (c: unknown) => (typeof c === 'string' && c.startsWith('#') ? c : COLOURS[Number(c) % COLOURS.length] ?? '#99b6c1');

const LabelsTab: React.FC<{ instance: string; connected: boolean }> = ({ instance, connected }) => {
  const q = useLineRead<unknown>(instance, 'label.findLabels', { enabled: connected });
  const labels = asArray<Obj>(q.data);
  const call = useLineActionCall();
  const invalidate = useInvalidateLineRead();
  const [number, setNumber] = useState('');
  const [labelId, setLabelId] = useState('');
  const [ask, setAsk] = useState<'add' | 'remove' | null>(null);
  const chosen = labels.find((l) => String(l.id) === labelId);
  const digits = digitsOf(number);

  const submit = () =>
    ask &&
    call.mutate(
      { instance, action: 'label.handleLabel', body: { number: digits, labelId, action: ask } },
      {
        onSuccess: () => {
          toast.success(ask === 'add' ? 'Label assigned' : 'Label removed');
          setAsk(null);
          invalidate(instance, 'chat.findChats');
        },
        onError: (e) => { setAsk(null); toastError(e, 'Could not change the label'); },
      },
    );

  if (!connected) return <Empty title="No phone is linked" hint="Link a phone from the Overview tab to see its labels." />;
  return (
    <div className="space-y-6">
      <Section title="Labels on this line">
        {q.isLoading && <Loading what="labels" />}
        {q.isError && <ErrorNote error={q.error} what="labels" />}
        {q.isSuccess && labels.length === 0 && <Empty title="No labels yet" hint="Labels are a WhatsApp Business feature. Create them in the Business app on the linked phone and they appear here." />}
        <ul className="flex flex-wrap gap-2">
          {labels.map((l) => (
            <li key={l.id} className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm">
              <span className="h-3 w-3 rounded-full" style={{ background: colourOf(l.color) }} />
              {l.name}
            </li>
          ))}
        </ul>
      </Section>

      {labels.length > 0 && (
        <Section title="Assign a label to a chat">
          <div className="space-y-3">
            <div><Label>Phone number</Label><Input placeholder="233241234567" value={number} onChange={(e) => setNumber(e.target.value)} /></div>
            <div>
              <Label>Label</Label>
              <select className={selectCls} value={labelId} onChange={(e) => setLabelId(e.target.value)}>
                <option value="">Choose a label</option>
                {labels.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <ActionButton action="label.handleLabel" disabled={digits.length < 8 || !labelId} onClick={() => setAsk('add')}><Tag /> Assign</ActionButton>
              <ActionButton action="label.handleLabel" variant="outline" disabled={digits.length < 8 || !labelId} onClick={() => setAsk('remove')}>Unassign</ActionButton>
            </div>
          </div>
        </Section>
      )}

      <ConfirmDialog
        open={!!ask}
        title={ask === 'add' ? 'Assign this label?' : 'Remove this label?'}
        body={`${ask === 'add' ? 'Label' : 'Remove label'} "${chosen?.name ?? ''}" ${ask === 'add' ? 'is added to' : 'from'} the chat with +${digits}.`}
        action={ask === 'add' ? 'Assign' : 'Remove'}
        pending={call.isPending}
        onConfirm={submit}
        onClose={() => setAsk(null)}
      />
    </div>
  );
};

export default LabelsTab;
